import { base64UrlToBytes, bytesToBase64Url } from '../../lib/tools/encoding';
import {
  AES_GCM_IV_BYTES,
  decryptAesGcm,
  derivePbkdf2KeyBytes,
  encryptAesGcm,
  randomBytes,
} from '../../lib/tools/crypto';
import {
  buildSafFileHeader,
  decodeUint32,
  deriveChunkNonce,
  encodeUint32,
  parseSafFileHeader,
  SAF_CHUNK_SIZE,
  SAF_PBKDF2_ITERATIONS,
  type SafHeader,
} from '../../lib/tools/saf';

const tabButtons = document.querySelectorAll<HTMLButtonElement>('[data-saf-tab]');
const tabPanels = document.querySelectorAll<HTMLElement>('[data-saf-panel]');

const encryptInput = document.querySelector<HTMLInputElement>('#saf-encrypt-file');
const encryptDrop = document.querySelector<HTMLLabelElement>('#saf-encrypt-drop');
const encryptPassphrase = document.querySelector<HTMLInputElement>(
  '#saf-encrypt-passphrase'
);
const encryptButton = document.querySelector<HTMLButtonElement>('#saf-encrypt-button');
const encryptProgress = document.querySelector<HTMLProgressElement>(
  '#saf-encrypt-progress'
);
const encryptStatus = document.querySelector<HTMLParagraphElement>('#saf-encrypt-status');
const encryptDownload = document.querySelector<HTMLAnchorElement>(
  '#saf-encrypt-download'
);

const decryptInput = document.querySelector<HTMLInputElement>('#saf-decrypt-file');
const decryptDrop = document.querySelector<HTMLLabelElement>('#saf-decrypt-drop');
const decryptPassphrase = document.querySelector<HTMLInputElement>(
  '#saf-decrypt-passphrase'
);
const decryptButton = document.querySelector<HTMLButtonElement>('#saf-decrypt-button');
const decryptProgress = document.querySelector<HTMLProgressElement>(
  '#saf-decrypt-progress'
);
const decryptStatus = document.querySelector<HTMLParagraphElement>('#saf-decrypt-status');
const decryptDownload = document.querySelector<HTMLAnchorElement>(
  '#saf-decrypt-download'
);

if (
  tabButtons.length === 0 ||
  tabPanels.length === 0 ||
  !encryptInput ||
  !encryptDrop ||
  !encryptPassphrase ||
  !encryptButton ||
  !encryptProgress ||
  !encryptStatus ||
  !encryptDownload ||
  !decryptInput ||
  !decryptDrop ||
  !decryptPassphrase ||
  !decryptButton ||
  !decryptProgress ||
  !decryptStatus ||
  !decryptDownload
) {
  throw new Error('SAF tool elements not found.');
}

let encryptFile: File | null = null;
let decryptFile: File | null = null;
let encryptUrl: string | null = null;
let decryptUrl: string | null = null;

function setStatus(element: HTMLElement, message: string) {
  element.textContent = message;
}

function resetProgress(progress: HTMLProgressElement, total: number) {
  progress.value = 0;
  progress.max = total;
}

function switchTab(target: string) {
  tabButtons.forEach((button) => {
    const isActive = button.dataset.safTab === target;
    button.setAttribute('aria-selected', isActive ? 'true' : 'false');
    button.classList.toggle('border-accent', isActive);
    button.classList.toggle('text-accent', isActive);
  });
  tabPanels.forEach((panel) => {
    panel.classList.toggle('hidden', panel.dataset.safPanel !== target);
  });
}

tabButtons.forEach((button) => {
  button.addEventListener('click', () => switchTab(button.dataset.safTab ?? 'encrypt'));
});

switchTab('encrypt');

function bindDropZone(
  dropZone: HTMLLabelElement,
  input: HTMLInputElement,
  onFile: (file: File) => void
) {
  dropZone.addEventListener('dragover', (event) => {
    event.preventDefault();
    dropZone.classList.add('border-accent');
  });
  dropZone.addEventListener('dragleave', () =>
    dropZone.classList.remove('border-accent')
  );
  dropZone.addEventListener('drop', (event) => {
    event.preventDefault();
    dropZone.classList.remove('border-accent');
    const file = event.dataTransfer?.files?.[0];
    if (file) {
      onFile(file);
    }
  });
  input.addEventListener('change', () => {
    const file = input.files?.[0];
    if (file) {
      onFile(file);
    }
  });
}

bindDropZone(encryptDrop, encryptInput, (file) => {
  encryptFile = file;
  if (encryptUrl) {
    URL.revokeObjectURL(encryptUrl);
    encryptUrl = null;
  }
  encryptDownload.classList.add('hidden');
  setStatus(
    encryptStatus,
    `Selected ${file.name} (${file.size.toLocaleString()} bytes).`
  );
});

bindDropZone(decryptDrop, decryptInput, (file) => {
  decryptFile = file;
  if (decryptUrl) {
    URL.revokeObjectURL(decryptUrl);
    decryptUrl = null;
  }
  decryptDownload.classList.add('hidden');
  setStatus(
    decryptStatus,
    `Selected ${file.name} (${file.size.toLocaleString()} bytes).`
  );
});

encryptButton.addEventListener('click', async () => {
  if (!encryptFile) {
    setStatus(encryptStatus, 'Choose a file to encrypt.');
    return;
  }
  const passphrase = encryptPassphrase.value.trim();
  if (!passphrase) {
    setStatus(encryptStatus, 'Passphrase is required.');
    return;
  }

  encryptButton.disabled = true;
  setStatus(encryptStatus, 'Deriving key...');

  const salt = randomBytes(16);
  const baseNonce = randomBytes(AES_GCM_IV_BYTES);
  baseNonce.set(new Uint8Array(4), 8);

  const keyBytes = await derivePbkdf2KeyBytes(passphrase, salt, SAF_PBKDF2_ITERATIONS);

  const header: SafHeader = {
    originalFilename: encryptFile.name,
    originalSize: encryptFile.size,
    chunkSize: SAF_CHUNK_SIZE,
    kdf: {
      name: 'PBKDF2',
      hash: 'SHA-256',
      iterations: SAF_PBKDF2_ITERATIONS,
      salt: bytesToBase64Url(salt),
    },
    alg: 'AES-GCM',
    baseNonce: bytesToBase64Url(baseNonce),
    createdAt: new Date().toISOString(),
  };

  const headerBytes = buildSafFileHeader(header);
  const parts: BlobPart[] = [headerBytes];
  resetProgress(encryptProgress, encryptFile.size);

  let offset = 0;
  let index = 0;
  while (offset < encryptFile.size) {
    const chunk = encryptFile.slice(offset, offset + SAF_CHUNK_SIZE);
    const chunkBuffer = await chunk.arrayBuffer();
    const iv = deriveChunkNonce(baseNonce, index);
    const ciphertext = await encryptAesGcm(keyBytes, iv, new Uint8Array(chunkBuffer));
    parts.push(encodeUint32(ciphertext.length));
    parts.push(ciphertext);
    offset += SAF_CHUNK_SIZE;
    index += 1;
    encryptProgress.value = Math.min(offset, encryptFile.size);
  }

  const blob = new Blob(parts, { type: 'application/octet-stream' });
  if (encryptUrl) {
    URL.revokeObjectURL(encryptUrl);
  }
  encryptUrl = URL.createObjectURL(blob);
  encryptDownload.href = encryptUrl;
  const downloadName = encryptFile.name.endsWith('.saf')
    ? encryptFile.name
    : `${encryptFile.name}.saf`;
  encryptDownload.download = downloadName;
  encryptDownload.classList.remove('hidden');
  setStatus(encryptStatus, 'Encryption complete. Download your .saf file.');
  encryptButton.disabled = false;
});

decryptButton.addEventListener('click', async () => {
  if (!decryptFile) {
    setStatus(decryptStatus, 'Choose a .saf file to decrypt.');
    return;
  }
  const passphrase = decryptPassphrase.value.trim();
  if (!passphrase) {
    setStatus(decryptStatus, 'Passphrase is required.');
    return;
  }

  decryptButton.disabled = true;
  setStatus(decryptStatus, 'Reading header...');
  resetProgress(decryptProgress, decryptFile.size);

  try {
    const prefixBuffer = await decryptFile.slice(0, 9).arrayBuffer();
    const headerLength = decodeUint32(new Uint8Array(prefixBuffer).slice(5, 9));
    const headerBuffer = await decryptFile.slice(0, 9 + headerLength).arrayBuffer();
    const { header, headerOffset } = parseSafFileHeader(new Uint8Array(headerBuffer));

    const salt = base64UrlToBytes(header.kdf.salt);
    const baseNonce = base64UrlToBytes(header.baseNonce);
    const keyBytes = await derivePbkdf2KeyBytes(passphrase, salt, header.kdf.iterations);

    const parts: BlobPart[] = [];
    let offset = headerOffset;
    let index = 0;

    while (offset < decryptFile.size) {
      const lengthBuffer = await decryptFile.slice(offset, offset + 4).arrayBuffer();
      const chunkLength = decodeUint32(new Uint8Array(lengthBuffer));
      offset += 4;
      const chunkBuffer = await decryptFile
        .slice(offset, offset + chunkLength)
        .arrayBuffer();
      offset += chunkLength;
      const iv = deriveChunkNonce(baseNonce, index);
      const plaintext = await decryptAesGcm(keyBytes, iv, new Uint8Array(chunkBuffer));
      parts.push(plaintext);
      index += 1;
      decryptProgress.value = Math.min(offset, decryptFile.size);
    }

    const blob = new Blob(parts, { type: 'application/octet-stream' });
    if (decryptUrl) {
      URL.revokeObjectURL(decryptUrl);
    }
    decryptUrl = URL.createObjectURL(blob);
    decryptDownload.href = decryptUrl;
    decryptDownload.download = header.originalFilename;
    decryptDownload.classList.remove('hidden');
    setStatus(decryptStatus, 'Decryption complete. Download the original file.');
  } catch (error) {
    setStatus(
      decryptStatus,
      'Decryption failed. Check the passphrase or file integrity.'
    );
  } finally {
    decryptButton.disabled = false;
  }
});
